import { useState, useEffect, useCallback } from "react";
import {
    Box,
    Card,
    Typography,
    Stack,
    Chip,
    Avatar,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Collapse,
    TextField,
    Pagination,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import ViewListRoundedIcon from "@mui/icons-material/ViewListRounded";
import ToggleOnRoundedIcon from "@mui/icons-material/ToggleOnRounded";
import ToggleOffRoundedIcon from "@mui/icons-material/ToggleOffRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import {
    getDownline,
    getDownlineTree,
    getDownlineUser,
    toggleUserStatus,
    updateUserLimits,
} from "../api";
import Tree from "react-d3-tree";

const ROLE_COLORS = {
    ADMIN: "#1c6b8c",
    ADMIN_PARTNER: "#2f8fb5",
    WHITE_LABEL: "#a855f7",
    STATE_HEAD: "#0ea5e9",
    SUPER_DISTRIBUTOR: "#06b6d4",
    MASTER_DISTRIBUTOR: "#14b8a6",
    DISTRIBUTOR: "#22c55e",
    RETAILER: "#f59e0b",
    SHOPKEEPER: "#f97316",
};

function formatDate(val) {
    if (val == null) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

function ExpandableRow({ row, onRefresh }) {
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [limitsOpen, setLimitsOpen] = useState(false);
    const [limits, setLimits] = useState({
        depositLimit: "",
        withdrawDailyLimit: "",
    });
    const [saving, setSaving] = useState(false);
    const firstName = row.first_name ?? row.firstName ?? "";
    const lastName = row.last_name ?? row.lastName ?? "";
    const roleName = row.role_name ?? row.roleName;
    const isActive = row.is_active ?? row.isActive ?? true;
    const depositLimit = row.deposit_limit ?? row.depositLimit;
    const withdrawDailyLimit =
        row.withdraw_daily_limit ?? row.withdrawDailyLimit;

    const handleExpand = async () => {
        if (open) {
            setOpen(false);
            return;
        }
        setOpen(true);
        if (!detail) {
            setLoadingDetail(true);
            try {
                const { data } = await getDownlineUser(row.id);
                setDetail(
                    data && typeof data === "object" && data.user
                        ? data.user
                        : data,
                );
            } catch {
                /* ignore */
            }
            setLoadingDetail(false);
        }
    };

    const handleToggleStatus = async () => {
        try {
            await toggleUserStatus(row.id, !isActive);
            onRefresh();
        } catch {
            /* ignore */
        }
    };

    const handleSaveLimits = async () => {
        setSaving(true);
        try {
            await updateUserLimits(row.id, {
                depositLimit: limits.depositLimit
                    ? parseFloat(limits.depositLimit)
                    : undefined,
                withdrawDailyLimit: limits.withdrawDailyLimit
                    ? parseFloat(limits.withdrawDailyLimit)
                    : undefined,
            });
            setLimitsOpen(false);
            onRefresh();
        } catch {
            /* ignore */
        }
        setSaving(false);
    };

    return (
        <>
            <TableRow
                hover
                sx={{ "& > *": { borderBottom: open ? "none" : undefined } }}
            >
                <TableCell padding="checkbox">
                    <IconButton size="small" onClick={handleExpand}>
                        {open ? (
                            <KeyboardArrowUpRoundedIcon />
                        ) : (
                            <KeyboardArrowDownRoundedIcon />
                        )}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                        <Avatar
                            sx={{
                                width: 32,
                                height: 32,
                                bgcolor: ROLE_COLORS[roleName] || "#1c6b8c",
                                fontSize: 13,
                                fontWeight: 700,
                            }}
                        >
                            {firstName[0]}
                            {lastName[0]}
                        </Avatar>
                        <Box>
                            <Typography variant="body2" fontWeight={600}>
                                {firstName} {lastName}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {row.email || row.mobile_number || "—"}
                            </Typography>
                        </Box>
                    </Stack>
                </TableCell>
                <TableCell>
                    <Chip
                        size="small"
                        label={(roleName ?? "").replace(/_/g, " ")}
                        sx={{
                            bgcolor: `${ROLE_COLORS[roleName] || "#1c6b8c"}15`,
                            color: ROLE_COLORS[roleName] || "#1c6b8c",
                            fontWeight: 600,
                            fontSize: 11,
                        }}
                    />
                </TableCell>
                <TableCell>
                    <Chip
                        size="small"
                        label={isActive ? "Active" : "Inactive"}
                        color={isActive ? "success" : "default"}
                    />
                </TableCell>
                <TableCell align="right">
                    <Stack direction="row" gap={0.5} justifyContent="flex-end">
                        <IconButton
                            size="small"
                            onClick={handleToggleStatus}
                            color={isActive ? "error" : "success"}
                            title={isActive ? "Deactivate" : "Activate"}
                        >
                            {isActive ? (
                                <ToggleOffRoundedIcon />
                            ) : (
                                <ToggleOnRoundedIcon />
                            )}
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => {
                                setLimits({
                                    depositLimit: depositLimit ?? "",
                                    withdrawDailyLimit:
                                        withdrawDailyLimit ?? "",
                                });
                                setLimitsOpen(true);
                            }}
                            title="Edit Limits"
                        >
                            <EditRoundedIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={5} sx={{ py: 0 }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 2 }}>
                            {loadingDetail ? (
                                <CircularProgress size={20} />
                            ) : detail ? (
                                <Stack direction="row" gap={4} flexWrap="wrap">
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Phone
                                        </Typography>
                                        <Typography variant="body2">
                                            {detail.mobile_number ??
                                                detail.mobileNumber ??
                                                "—"}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            City
                                        </Typography>
                                        <Typography variant="body2">
                                            {detail.city ?? "—"}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            State
                                        </Typography>
                                        <Typography variant="body2">
                                            {detail.state ?? "—"}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            PAN
                                        </Typography>
                                        <Typography variant="body2">
                                            {detail.pan_card_number ??
                                                detail.panCardNumber ??
                                                "—"}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Deposit Limit
                                        </Typography>
                                        <Typography variant="body2">
                                            ₹
                                            {Number(
                                                detail.deposit_limit ??
                                                    detail.depositLimit ??
                                                    0,
                                            ).toLocaleString("en-IN")}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Withdraw Limit
                                        </Typography>
                                        <Typography variant="body2">
                                            ₹
                                            {Number(
                                                detail.withdraw_daily_limit ??
                                                    detail.withdrawDailyLimit ??
                                                    0,
                                            ).toLocaleString("en-IN")}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Joined
                                        </Typography>
                                        <Typography variant="body2">
                                            {formatDate(
                                                detail.created_at ??
                                                    detail.createdAt,
                                            )}
                                        </Typography>
                                    </Box>
                                </Stack>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No details
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>

            <Dialog
                open={limitsOpen}
                onClose={() => setLimitsOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle fontWeight={600}>
                    Edit Limits — {firstName} {lastName}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField
                            fullWidth
                            label="Deposit Limit"
                            type="number"
                            value={limits.depositLimit}
                            onChange={(e) =>
                                setLimits({
                                    ...limits,
                                    depositLimit: e.target.value,
                                })
                            }
                        />
                        <TextField
                            fullWidth
                            label="Daily Withdraw Limit"
                            type="number"
                            value={limits.withdrawDailyLimit}
                            onChange={(e) =>
                                setLimits({
                                    ...limits,
                                    withdrawDailyLimit: e.target.value,
                                })
                            }
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setLimitsOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveLimits}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default function Downline() {
    const [view, setView] = useState("list"); // "list" | "tree"
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [treeData, setTreeData] = useState(null);
    const [treeLoading, setTreeLoading] = useState(false);

    const fetchData = useCallback(() => {
        setLoading(true);
        getDownline(page, 20)
            .then(({ data }) => {
                setUsers(data.users || []);
                setTotal(data.pagination?.total ?? data.total ?? 0);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [page]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (view !== "tree") return;
        setTreeLoading(true);
        getDownlineTree()
            .then(({ data }) => {
                const t = data?.tree;
                if (t && (t.children?.length || t.name)) {
                    setTreeData(transformForD3(t));
                } else {
                    setTreeData({
                        name: "You",
                        attributes: { role: "No downline yet" },
                    });
                }
            })
            .catch(() =>
                setTreeData({
                    name: "You",
                    attributes: { role: "Could not load tree" },
                }),
            )
            .finally(() => setTreeLoading(false));
    }, [view]);

    return (
        <Box>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
                flexWrap="wrap"
                gap={2}
            >
                <Box>
                    <Typography variant="h5">My Downline</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {total} users in your network
                    </Typography>
                </Box>
                <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={(_, v) => v && setView(v)}
                    size="small"
                    sx={{
                        "& .MuiToggleButton-root": {
                            borderRadius: 2,
                            fontWeight: 600,
                        },
                    }}
                >
                    <ToggleButton value="list">
                        <ViewListRoundedIcon sx={{ mr: 1, fontSize: 18 }} />
                        List
                    </ToggleButton>
                    <ToggleButton value="tree">
                        <AccountTreeRoundedIcon sx={{ mr: 1, fontSize: 18 }} />
                        Org chart
                    </ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {view === "tree" ? (
                <Card sx={{ overflow: "hidden", minHeight: 400 }}>
                    {treeLoading ? (
                        <Box sx={{ p: 5, textAlign: "center" }}>
                            <CircularProgress />
                        </Box>
                    ) : treeData ? (
                        <Box sx={{ width: "100%", height: 500 }}>
                            <Tree
                                data={treeData}
                                rootNodeClassName="downline-root"
                                nodeClassName="downline-node"
                                pathClassFunc={() => "downline-link"}
                                translate={{ x: 320, y: 40 }}
                                separation={{ siblings: 1.2, nonSiblings: 1.5 }}
                                nodeSize={{ x: 140, y: 80 }}
                                enableLegacyTransitions
                                renderCustomNodeElement={({ nodeDatum }) => (
                                    <g>
                                        <circle
                                            r={24}
                                            fill="#1c6b8c"
                                            stroke="#1c6b8c"
                                            strokeWidth={2}
                                        />
                                        <text
                                            fill="white"
                                            strokeWidth={0}
                                            x={0}
                                            y={5}
                                            textAnchor="middle"
                                            fontSize={11}
                                            fontWeight={600}
                                        >
                                            {(nodeDatum.name || "")
                                                .slice(0, 2)
                                                .toUpperCase()}
                                        </text>
                                        <text
                                            x={0}
                                            y={36}
                                            textAnchor="middle"
                                            fontSize={12}
                                            fontWeight={600}
                                            fill="#1e293b"
                                        >
                                            {nodeDatum.name}
                                        </text>
                                        {nodeDatum.attributes?.role && (
                                            <text
                                                x={0}
                                                y={52}
                                                textAnchor="middle"
                                                fontSize={10}
                                                fill="#64748b"
                                            >
                                                {nodeDatum.attributes.role}
                                            </text>
                                        )}
                                    </g>
                                )}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ p: 5, textAlign: "center" }}>
                            <AccountTreeRoundedIcon
                                sx={{
                                    fontSize: 48,
                                    color: "text.disabled",
                                    mb: 1,
                                }}
                            />
                            <Typography color="text.secondary">
                                No downline yet
                            </Typography>
                        </Box>
                    )}
                </Card>
            ) : (
                <Card sx={{ overflow: "hidden" }}>
                    {loading ? (
                        <Box sx={{ p: 5, textAlign: "center" }}>
                            <CircularProgress />
                        </Box>
                    ) : users.length === 0 ? (
                        <Box sx={{ p: 5, textAlign: "center" }}>
                            <AccountTreeRoundedIcon
                                sx={{
                                    fontSize: 48,
                                    color: "text.disabled",
                                    mb: 1,
                                }}
                            />
                            <Typography color="text.secondary">
                                No users in your downline yet
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ overflowX: "auto" }}>
                            <Table sx={{ minWidth: 640 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell width={40} />
                                        <TableCell>User</TableCell>
                                        <TableCell>Role</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((u) => (
                                        <ExpandableRow
                                            key={u.id}
                                            row={u}
                                            onRefresh={fetchData}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                            </Box>
                            {Math.ceil(total / 20) > 1 && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        py: 2,
                                    }}
                                >
                                    <Pagination
                                        count={Math.ceil(total / 20)}
                                        page={page}
                                        onChange={(_, v) => setPage(v)}
                                        color="primary"
                                    />
                                </Box>
                            )}
                        </>
                    )}
                </Card>
            )}
        </Box>
    );
}

/** Transform API tree (name, roleName, children) to react-d3-tree format (name, attributes, children) */
function transformForD3(node) {
    if (!node) return { name: "—" };
    const out = {
        name: node.name || "—",
        ...(node.roleName
            ? { attributes: { role: (node.roleName || "").replace(/_/g, " ") } }
            : {}),
    };
    if (node.children && node.children.length > 0) {
        out.children = node.children.map(transformForD3);
    }
    return out;
}
